import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { automunityAPI } from '../lib/api';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import './RequestView.css';

interface RequestDetail {
  request_id: number;
  automation_id: number;
  automation_name?: string;
  type: string;
  priority: string;
  status: string;
  title: string;
  description?: string;
  steps_to_reproduce?: string;
  expected_behavior?: string;
  submitted_by?: number;
  submitted_by_name?: string;
  assigned_to?: number;
  assigned_to_name?: string;
  resolution_notes?: string;
  kanban_column?: string;
  kanban_order?: number;
  deadline?: string;
  created_at: string;
  updated_at?: string;
  resolved_at?: string;
}

interface Comment {
  comment_id: number;
  request_id: number;
  user_id?: number;
  user_name?: string;
  comment: string;
  created_at: string;
}

interface TimeEntry {
  time_entry_id: number;
  request_id: number;
  user_id?: number;
  user_name?: string;
  hours: number;
  notes?: string;
  work_date: string;
  created_at: string;
}

interface Task {
  task_id: number;
  request_id: number;
  title: string;
  detail?: string;
  category: string;
  requires_human: number;
  environment?: string | null;
  blocks_production: number;
  status: string;
  sort_order: number;
  completed_by_name?: string;
  completed_at?: string | null;
}

interface Automation {
  automation_id: number;
  name: string;
}

interface User {
  user_id: number;
  full_name: string;
  is_developer?: number;
}

interface StatusConfig {
  name: string;
  label: string;
  color: string;
}

const PRIORITY_COLORS: Record<string, string> = {
  critical: '#dc3545',
  high: '#fd7e14',
  medium: '#ffc107',
  low: '#6c757d',
};

const TYPE_ICONS: Record<string, string> = {
  feature: '✨',
  bug: '🐛',
  improvement: '🔧',
  question: '❓',
  administrative: '📋',
};

const RequestView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [request, setRequest] = useState<RequestDetail | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [statuses, setStatuses] = useState<StatusConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [newTimeEntry, setNewTimeEntry] = useState({ hours: '', notes: '', work_date: new Date().toISOString().split('T')[0] });
  const [activityTab, setActivityTab] = useState<'time' | 'comments' | 'tasks'>('time');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [newTask, setNewTask] = useState({
    title: '', category: 'general', environment: '', requires_human: false, blocks_production: false
  });
  const [addingTask, setAddingTask] = useState(false);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [convertingToAutomation, setConvertingToAutomation] = useState(false);
  const [watchers, setWatchers] = useState<{ watcher_id: number; user_id: number; user_name: string; user_email: string }[]>([]);
  const [addingWatcher, setAddingWatcher] = useState(false);

  const fetchRequest = useCallback(async () => {
    if (!id) return;

    // There's no single-request endpoint without an automation_id, so we search
    // the list endpoints. The default list returns only OPEN/active requests, so
    // a closed/resolved/legacy request (e.g. a Resolved-tab link) won't be found
    // there — fall back to the closed/resolved set before giving up.
    const parseList = (result: any): RequestDetail[] => {
      if (Array.isArray(result)) return result;
      if (Array.isArray(result?.records)) return result.records;
      if (typeof result?.records === 'string') { try { return JSON.parse(result.records); } catch { return []; } }
      return [];
    };

    try {
      // Pass 1: open/active requests (fast, common case)
      const openRes = await automunityAPI.getAllRequests();
      const openData = parseList(await openRes.body.json() as any);
      let found = openData.find(r => r.request_id === Number(id));

      // Pass 2: closed/resolved history (only if not in the open set)
      if (!found) {
        const closedRes = await automunityAPI.getAllRequests({ includeClosed: true });
        const closedData = parseList(await closedRes.body.json() as any);
        found = closedData.find(r => r.request_id === Number(id));
      }

      if (found) setRequest(found);
    } catch (err) {
      console.error('Error fetching request:', err);
    }
  }, [id]);

  const fetchComments = useCallback(async () => {
    if (!id) return;
    try {
      const response = await automunityAPI.getRequestComments(Number(id));
      const result = await response.body.json() as any;
      let data: Comment[] = [];
      if (Array.isArray(result)) data = result;
      else if (Array.isArray(result?.records)) data = result.records;
      else if (typeof result?.records === 'string') data = JSON.parse(result.records);
      setComments(data);
    } catch (err) {
      console.error('Error fetching comments:', err);
    }
  }, [id]);

  const fetchTimeEntries = useCallback(async () => {
    if (!id) return;
    try {
      const response = await automunityAPI.getRequestTimeEntries(Number(id));
      const result = await response.body.json() as any;
      let data: TimeEntry[] = [];
      if (Array.isArray(result)) data = result;
      else if (Array.isArray(result?.records)) data = result.records;
      else if (typeof result?.records === 'string') data = JSON.parse(result.records);
      setTimeEntries(data);
    } catch (err) {
      console.error('Error fetching time entries:', err);
    }
  }, [id]);

  const fetchTasks = useCallback(async () => {
    if (!id) return;
    try {
      const response = await automunityAPI.getRequestTasks(Number(id));
      const result = await response.body.json() as any;
      let data: Task[] = [];
      if (Array.isArray(result)) data = result;
      else if (Array.isArray(result?.records)) data = result.records;
      else if (typeof result?.records === 'string') data = JSON.parse(result.records);
      setTasks(data);
    } catch (err) {
      console.error('Error fetching tasks:', err);
    }
  }, [id]);

  const fetchAttachments = useCallback(async () => {
    if (!id) return;
    try {
      const response = await automunityAPI.getAttachments(Number(id));
      const result = await response.body.json() as any;
      let data: any[] = [];
      if (Array.isArray(result)) data = result;
      else if (Array.isArray(result?.records)) data = result.records;
      else if (typeof result?.records === 'string') data = JSON.parse(result.records);
      setAttachments(data);
    } catch (err) {
      console.error('Error fetching attachments:', err);
    }
  }, [id]);

  const fetchWatchers = useCallback(async () => {
    if (!id) return;
    try {
      const response = await automunityAPI.getRequestWatchers(Number(id));
      const result = await response.body.json() as any;
      let data: any[] = [];
      if (Array.isArray(result)) data = result;
      else if (Array.isArray(result?.records)) data = result.records;
      else if (typeof result?.records === 'string') data = JSON.parse(result.records);
      setWatchers(data);
    } catch (err) {
      console.error('Error fetching watchers:', err);
    }
  }, [id]);

  const fetchAutomations = useCallback(async () => {
    try {
      const response = await automunityAPI.getAutomations();
      const result = await response.body.json() as any;
      let data: Automation[] = [];
      if (Array.isArray(result)) data = result;
      else if (Array.isArray(result?.records)) data = result.records;
      else if (typeof result?.records === 'string') data = JSON.parse(result.records);
      setAutomations(data.filter((a: any) => a && a.automation_id));
    } catch (err) {
      console.error('Error fetching automations:', err);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await automunityAPI.getUsers();
      const result = await response.body.json() as any;
      let data: User[] = [];
      if (Array.isArray(result)) data = result;
      else if (Array.isArray(result?.records)) data = result.records;
      else if (typeof result?.records === 'string') data = JSON.parse(result.records);
      setUsers(data.filter((u: any) => u && u.user_id && u.is_active));
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, []);

  const fetchStatuses = useCallback(async () => {
    try {
      const response = await automunityAPI.getRequestStatuses();
      const result = await response.body.json() as any;
      let data: StatusConfig[] = [];
      if (Array.isArray(result)) data = result;
      else if (Array.isArray(result?.records)) data = result.records;
      else if (typeof result?.records === 'string') data = JSON.parse(result.records);
      if (data.length > 0) setStatuses(data);
    } catch (err) { /* fallback handled below */ }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchRequest(), fetchComments(), fetchTimeEntries(), fetchTasks(), fetchAttachments(), fetchWatchers(), fetchAutomations(), fetchUsers(), fetchStatuses()]);
      setLoading(false);
    };
    loadAll();

    // Resolve logged-in user to a user_id
    const resolveCurrentUser = async () => {
      try {
        const session = await fetchAuthSession();
        const email = (session.tokens?.idToken?.payload?.email as string || '').toLowerCase();
        if (email) {
          const response = await automunityAPI.getUsers();
          const result = await response.body.json() as any;
          let userList: User[] = [];
          if (Array.isArray(result)) userList = result;
          else if (Array.isArray(result?.records)) userList = result.records;
          else if (typeof result?.records === 'string') userList = JSON.parse(result.records);
          
          const match = userList.find(u => u.full_name && (u as any).email && (u as any).email.toLowerCase() === email);
          if (match) setCurrentUserId(match.user_id);
        }
      } catch { /* no-op */ }
    };
    resolveCurrentUser();
  }, [fetchRequest, fetchComments, fetchTimeEntries, fetchTasks, fetchAttachments, fetchWatchers, fetchAutomations, fetchUsers, fetchStatuses]);

  const handleUpdate = async (field: string, value: any) => {
    if (!request) return;
    try {
      const updatePayload: any = { [field]: value };
      // For status changes, include previous status for time tracking
      if (field === 'status') {
        updatePayload._previous_status = request.status;
      }
      await automunityAPI.updateRequest(String(request.automation_id), request.request_id, updatePayload);
      setRequest(prev => prev ? { ...prev, [field]: value } : null);

      // If changing automation_id, also update the automation_name display
      if (field === 'automation_id') {
        const auto = automations.find(a => a.automation_id === Number(value));
        setRequest(prev => prev ? { ...prev, automation_id: Number(value), automation_name: auto?.name } : null);
      }
      if (field === 'assigned_to') {
        const user = users.find(u => u.user_id === Number(value));
        setRequest(prev => prev ? { ...prev, assigned_to: Number(value) || undefined, assigned_to_name: user?.full_name } : null);
      }
    } catch (err) {
      console.error(`Error updating ${field}:`, err);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !id) return;
    setSubmittingComment(true);
    try {
      await automunityAPI.createRequestComment(Number(id), { 
        comment: newComment,
        user_id: currentUserId
      });
      setNewComment('');
      fetchComments();
    } catch (err) {
      console.error('Error adding comment:', err);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleAddTimeEntry = async () => {
    if (!newTimeEntry.hours || !id) return;
    try {
      await automunityAPI.createTimeEntry(Number(id), {
        hours: parseFloat(newTimeEntry.hours),
        notes: newTimeEntry.notes,
        work_date: newTimeEntry.work_date,
        user_id: currentUserId
      });
      setNewTimeEntry({ hours: '', notes: '', work_date: new Date().toISOString().split('T')[0] });
      fetchTimeEntries();
    } catch (err) {
      console.error('Error adding time entry:', err);
    }
  };

  const handleAddTask = async () => {
    if (!newTask.title.trim() || !id) return;
    setAddingTask(true);
    try {
      await automunityAPI.createRequestTask(Number(id), {
        title: newTask.title.trim(),
        category: newTask.category,
        environment: newTask.environment || null,
        requires_human: newTask.requires_human ? 1 : 0,
        blocks_production: newTask.blocks_production ? 1 : 0,
        created_by: currentUserId
      });
      setNewTask({ title: '', category: 'general', environment: '', requires_human: false, blocks_production: false });
      fetchTasks();
    } catch (err) {
      console.error('Error adding task:', err);
      toast.error('Could not add task');
    } finally {
      setAddingTask(false);
    }
  };

  const handleToggleTask = async (task: Task) => {
    if (!id) return;
    const nextStatus = task.status === 'done' ? 'open' : 'done';
    try {
      await automunityAPI.updateRequestTask(Number(id), task.task_id, { status: nextStatus, completed_by: currentUserId });
      fetchTasks();
    } catch (err) {
      console.error('Error updating task:', err);
    }
  };

  const handleDeleteTask = async (taskId: number) => {
    if (!id) return;
    try {
      await automunityAPI.deleteRequestTask(Number(id), taskId);
      fetchTasks();
    } catch (err) {
      console.error('Error deleting task:', err);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !id) return;
    setUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        // Get presigned upload URL
        const urlRes = await automunityAPI.getUploadUrl({
          file_name: file.name,
          file_type: file.type || 'application/octet-stream',
          request_id: Number(id)
        });
        const urlData = await urlRes.body.json();
        const parsed = typeof urlData === 'string' ? JSON.parse(urlData) : urlData;
        if (!parsed.upload_url) { console.error('No upload URL returned'); continue; }

        // Upload directly to S3
        await fetch(parsed.upload_url, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'application/octet-stream' }
        });

        // Confirm the upload
        await automunityAPI.confirmAttachment({
          request_id: Number(id),
          s3_key: parsed.s3_key,
          file_name: file.name,
          file_type: file.type,
          file_size: file.size,
          uploaded_by: currentUserId,
          source: 'upload'
        });
      }
      fetchAttachments();
      toast.success(`${files.length} file(s) uploaded`);
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDownload = async (attachmentId: number) => {
    try {
      const res = await automunityAPI.getDownloadUrl(attachmentId);
      const data = await res.body.json();
      const parsed = typeof data === 'string' ? JSON.parse(data) : data;
      if (parsed.download_url) window.open(parsed.download_url, '_blank');
    } catch { toast.error('Failed to get download link'); }
  };

  const handleConvertToAutomation = async () => {
    if (!request) return;
    const confirmed = window.confirm(
      `Create a new automation from this request?\n\nName: ${request.title}\n\nThe request will be linked to the new automation.`
    );
    if (!confirmed) return;

    setConvertingToAutomation(true);
    try {
      const res = await automunityAPI.createAutomation({
        name: request.title,
        description: request.description || ''
      });
      const result = await res.body.json() as any;
      const parsed = typeof result === 'string' ? JSON.parse(result) : result;
      const newAutomationId = parsed.automation_id || parsed.id || parsed.records?.automation_id;

      if (newAutomationId) {
        // Link this request to the new automation
        await automunityAPI.updateRequest(String(request.automation_id), request.request_id, {
          automation_id: newAutomationId
        });
        toast.success('Automation created and request linked!');
        navigate(`/automations/${newAutomationId}`);
      } else {
        toast.success('Automation created!');
        navigate('/automations');
      }
    } catch (err) {
      console.error('Error converting to automation:', err);
      toast.error('Failed to create automation');
    } finally {
      setConvertingToAutomation(false);
    }
  };

  const totalHours = timeEntries.reduce((sum, te) => sum + Number(te.hours || 0), 0);
  const openTasks = tasks.filter(t => t.status === 'open' || t.status === 'in_progress');
  const openProdBlocking = openTasks.filter(t => t.blocks_production);

  const sortedUsers = [...users].sort((a, b) => {
    if (a.is_developer && !b.is_developer) return -1;
    if (!a.is_developer && b.is_developer) return 1;
    return (a.full_name || '').localeCompare(b.full_name || '');
  });

  const getStatusColor = (statusName: string) => {
    return statuses.find(s => s.name === statusName)?.color || '#6c757d';
  };

  const getStatusLabel = (statusName: string) => {
    return statuses.find(s => s.name === statusName)?.label || statusName;
  };

  if (loading) return <div className="request-view-loading">Loading request...</div>;
  if (!request) return <div className="request-view-loading">Request not found.</div>;

  return (
    <div className="request-view">
      <div className="request-view-header">
        <button className="back-btn" onClick={() => navigate('/requests')}>← Back to Requests</button>
        <div className="request-title-row">
          <span className="request-type-icon">{TYPE_ICONS[request.type] || '📝'}</span>
          <h1>
            #{request.request_id}:&nbsp;
            <span
              contentEditable
              suppressContentEditableWarning
              className="editable-title"
              onBlur={e => {
                const newTitle = (e.target as HTMLElement).innerText.trim();
                if (newTitle && newTitle !== request.title) handleUpdate('title', newTitle);
              }}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLElement).blur(); } }}
            >
              {request.title}
            </span>
          </h1>
        </div>
        <div className="request-meta">
          <span className="priority-badge" style={{ background: PRIORITY_COLORS[request.priority] }}>
            {request.priority}
          </span>
          <span className="status-badge" style={{ background: getStatusColor(request.status) }}>
            {getStatusLabel(request.status)}
          </span>
          <span className="request-type">{request.type}</span>
          {request.submitted_by_name && <span className="submitted-by">by {request.submitted_by_name}</span>}
          <span className="created-date">{new Date(request.created_at).toLocaleDateString()}</span>
        </div>
      </div>

      <div className="request-view-body">
        <div className="request-main">
          {openProdBlocking.length > 0 && (
            <div className="prod-blocking-banner" role="alert" style={{
              background: '#fff5f5', border: '1px solid #f5c2c7', borderLeft: '4px solid #dc3545',
              borderRadius: '6px', padding: '10px 14px', marginBottom: '14px', color: '#842029'
            }}>
              <strong>⚠ {openProdBlocking.length} task{openProdBlocking.length === 1 ? '' : 's'} must be completed before production:</strong>
              <ul style={{ margin: '6px 0 0', paddingLeft: '20px' }}>
                {openProdBlocking.map(t => (
                  <li key={t.task_id}>
                    {t.title}
                    {t.environment ? ` (${t.environment})` : ''}
                    {t.requires_human ? ' — human' : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="request-section">
            <h3>Description</h3>
            <textarea
              className="editable-description"
              value={request.description || ''}
              onChange={e => setRequest(prev => prev ? { ...prev, description: e.target.value } : null)}
              onBlur={e => handleUpdate('description', e.target.value)}
              placeholder="Add a description..."
              rows={4}
            />
          </div>

          {request.steps_to_reproduce && (
            <div className="request-section">
              <h3>Steps to Reproduce</h3>
              <p>{request.steps_to_reproduce}</p>
            </div>
          )}

          {request.expected_behavior && (
            <div className="request-section">
              <h3>Expected Behavior</h3>
              <p>{request.expected_behavior}</p>
            </div>
          )}

          {request.resolution_notes && (
            <div className="request-section">
              <h3>Resolution Notes</h3>
              <p>{request.resolution_notes}</p>
            </div>
          )}

          {/* Attachments Section */}
          <div className="request-section">
            <h3>📎 Attachments ({attachments.length})</h3>
            {attachments.length > 0 && (
              <div className="attachments-list">
                {attachments.map((att: any) => (
                  <div key={att.attachment_id} className="attachment-item" onClick={() => handleDownload(att.attachment_id)}>
                    <span className="att-icon">{att.file_type?.startsWith('image/') ? '🖼️' : '📄'}</span>
                    <span className="att-name">{att.file_name}</span>
                    <span className="att-size">{att.file_size ? `${(att.file_size / 1024).toFixed(0)} KB` : ''}</span>
                    <span className="att-source">{att.source === 'email' ? '✉️' : ''}</span>
                  </div>
                ))}
              </div>
            )}
            <label className="upload-btn" style={{ cursor: uploading ? 'wait' : 'pointer' }}>
              <input type="file" multiple onChange={handleFileUpload} style={{ display: 'none' }} disabled={uploading} />
              {uploading ? '⏳ Uploading...' : '📎 Attach Files'}
            </label>
          </div>

          {/* Activity Section — Time & Comments tabs */}
          <div className="request-section activity-section">
            <div className="activity-tabs">
              <button className={activityTab === 'time' ? 'active' : ''} onClick={() => setActivityTab('time')}>
                ⏱ Time ({totalHours.toFixed(1)}h)
              </button>
              <button className={activityTab === 'comments' ? 'active' : ''} onClick={() => setActivityTab('comments')}>
                💬 Comments ({comments.length})
              </button>
              <button className={activityTab === 'tasks' ? 'active' : ''} onClick={() => setActivityTab('tasks')}>
                ✅ Tasks ({openTasks.length}{openTasks.length !== tasks.length ? `/${tasks.length}` : ''})
                {openProdBlocking.length > 0 && <span title="Tasks block production" style={{ color: '#dc3545', marginLeft: 4 }}>●</span>}
              </button>
            </div>

            {activityTab === 'time' && (
              <div className="activity-content">
                {timeEntries.length > 0 && (
                  <table className="time-entries-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Hours</th>
                        <th>Who</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timeEntries.map(te => (
                        <tr key={te.time_entry_id}>
                          <td>{new Date(te.work_date).toLocaleDateString()}</td>
                          <td><strong>{Number(te.hours).toFixed(1)}h</strong></td>
                          <td>{te.user_name || 'Unknown'}</td>
                          <td>{te.notes ? <ReactMarkdown>{te.notes}</ReactMarkdown> : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <div className="time-entry-form">
                  <div className="time-entry-row">
                    <input
                      type="number"
                      step="0.25"
                      min="0.25"
                      placeholder="Hours"
                      value={newTimeEntry.hours}
                      onChange={e => setNewTimeEntry({ ...newTimeEntry, hours: e.target.value })}
                    />
                    <input
                      type="date"
                      value={newTimeEntry.work_date}
                      onChange={e => setNewTimeEntry({ ...newTimeEntry, work_date: e.target.value })}
                    />
                    <button onClick={handleAddTimeEntry} disabled={!newTimeEntry.hours}>
                      Log Time
                    </button>
                  </div>
                  <input
                    type="text"
                    placeholder="Notes (optional)"
                    value={newTimeEntry.notes}
                    onChange={e => setNewTimeEntry({ ...newTimeEntry, notes: e.target.value })}
                    className="time-entry-notes"
                  />
                </div>
              </div>
            )}

            {activityTab === 'comments' && (
              <div className="activity-content">
                <div className="comments-list">
                  {comments.length === 0 ? (
                    <p className="no-comments">No comments yet.</p>
                  ) : (
                    [...comments].reverse().map(c => (
                      <div key={c.comment_id} className="comment">
                        <div className="comment-header">
                          <span className="comment-author">{c.user_name || 'Unknown'}</span>
                          <span className="comment-date">{new Date(c.created_at).toLocaleString()}</span>
                        </div>
                        <div className="comment-body"><ReactMarkdown>{c.comment}</ReactMarkdown></div>
                      </div>
                    ))
                  )}
                </div>
                <div className="comment-input">
                  <textarea
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    rows={3}
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || submittingComment}
                  >
                    {submittingComment ? 'Posting...' : 'Post Comment'}
                  </button>
                </div>
              </div>
            )}

            {activityTab === 'tasks' && (
              <div className="activity-content">
                <p style={{ fontSize: '13px', color: '#666', margin: '0 0 12px' }}>
                  Discrete steps needed to ship this request. Flag human-only work and steps that
                  must be done before production (e.g. running a SQL migration in prod) so they
                  aren't forgotten at deploy time.
                </p>
                <div className="tasks-list">
                  {tasks.length === 0 ? (
                    <p className="no-comments">No tasks yet.</p>
                  ) : (
                    tasks.map(t => (
                      <div
                        key={t.task_id}
                        className="task-row"
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: '8px',
                          padding: '8px 0', borderBottom: '1px solid #edf2f7'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={t.status === 'done'}
                          onChange={() => handleToggleTask(t)}
                          style={{ marginTop: '3px' }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{
                            textDecoration: t.status === 'done' ? 'line-through' : 'none',
                            color: t.status === 'done' ? '#999' : '#1a202c'
                          }}>
                            {t.title}
                          </div>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
                            {!!t.blocks_production && (
                              <span style={{ background: '#dc3545', color: '#fff', borderRadius: '3px', padding: '1px 6px', fontSize: '11px', fontWeight: 600 }}>blocks prod</span>
                            )}
                            {!!t.requires_human && (
                              <span style={{ background: '#e2e8f0', color: '#2d3748', borderRadius: '3px', padding: '1px 6px', fontSize: '11px' }}>human</span>
                            )}
                            {t.environment && (
                              <span style={{ background: '#e7f1ff', color: '#0b5ed7', borderRadius: '3px', padding: '1px 6px', fontSize: '11px' }}>env: {t.environment}</span>
                            )}
                            {t.category && t.category !== 'general' && (
                              <span style={{ background: '#f0f0f0', color: '#555', borderRadius: '3px', padding: '1px 6px', fontSize: '11px' }}>{t.category}</span>
                            )}
                            {t.status === 'done' && t.completed_by_name && (
                              <span style={{ color: '#a0aec0', fontSize: '11px' }}>done by {t.completed_by_name}</span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteTask(t.task_id)}
                          title="Delete task"
                          style={{ background: 'none', border: 'none', color: '#a0aec0', cursor: 'pointer', fontSize: '16px', lineHeight: 1 }}
                        >
                          ×
                        </button>
                      </div>
                    ))
                  )}
                </div>

                <div className="task-form" style={{ marginTop: '12px' }}>
                  <input
                    type="text"
                    placeholder="New task (e.g. Run 2025_rts_optional_attachment.sql in prod)"
                    value={newTask.title}
                    onChange={e => setNewTask({ ...newTask, title: e.target.value })}
                    onKeyDown={e => { if (e.key === 'Enter') handleAddTask(); }}
                    style={{ width: '100%', padding: '8px', boxSizing: 'border-box', marginBottom: '8px' }}
                  />
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <select value={newTask.category} onChange={e => setNewTask({ ...newTask, category: e.target.value })}>
                      <option value="general">general</option>
                      <option value="sql_migration">sql_migration</option>
                      <option value="sql_query">sql_query</option>
                      <option value="config">config</option>
                      <option value="deploy">deploy</option>
                      <option value="verification">verification</option>
                      <option value="manual">manual</option>
                    </select>
                    <select value={newTask.environment} onChange={e => setNewTask({ ...newTask, environment: e.target.value })}>
                      <option value="">env: n/a</option>
                      <option value="dev">dev</option>
                      <option value="prod">prod</option>
                      <option value="both">both</option>
                    </select>
                    <label style={{ fontSize: '13px' }}>
                      <input type="checkbox" checked={newTask.requires_human} onChange={e => setNewTask({ ...newTask, requires_human: e.target.checked })} /> human
                    </label>
                    <label style={{ fontSize: '13px' }}>
                      <input type="checkbox" checked={newTask.blocks_production} onChange={e => setNewTask({ ...newTask, blocks_production: e.target.checked })} /> blocks prod
                    </label>
                    <button onClick={handleAddTask} disabled={!newTask.title.trim() || addingTask}>
                      {addingTask ? 'Adding...' : 'Add Task'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="request-sidebar">
          <div className="sidebar-field">
            <label>Status</label>
            <select
              value={request.status}
              onChange={e => handleUpdate('status', e.target.value)}
              style={{ borderLeft: `3px solid ${getStatusColor(request.status)}` }}
            >
              {statuses.map(s => (
                <option key={s.name} value={s.name}>{s.label}</option>
              ))}
            </select>
          </div>

          <div className="sidebar-field">
            <label>Automation</label>
            <select
              value={request.automation_id || ''}
              onChange={e => handleUpdate('automation_id', Number(e.target.value))}
            >
              <option value="">— Select Automation —</option>
              {automations.sort((a, b) => a.name.localeCompare(b.name)).map(a => (
                <option key={a.automation_id} value={a.automation_id}>{a.name}</option>
              ))}
            </select>
          </div>

          <div className="sidebar-field">
            <label>Assigned To</label>
            <select
              value={request.assigned_to || ''}
              onChange={e => handleUpdate('assigned_to', e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Unassigned</option>
              {sortedUsers.map(u => (
                <option key={u.user_id} value={u.user_id}>
                  {u.full_name}
                </option>
              ))}
            </select>
          </div>

          <div className="sidebar-field">
            <label>Requested By</label>
            <select
              value={request.submitted_by || ''}
              onChange={e => handleUpdate('submitted_by', e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">— Select —</option>
              {sortedUsers.map(u => (
                <option key={u.user_id} value={u.user_id}>
                  {u.full_name}
                </option>
              ))}
            </select>
          </div>

          <div className="sidebar-field">
            <label>Priority</label>
            <select
              value={request.priority}
              onChange={e => handleUpdate('priority', e.target.value)}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>

          <div className="sidebar-field">
            <label>Type</label>
            <select
              value={request.type}
              onChange={e => handleUpdate('type', e.target.value)}
            >
              <option value="feature">Feature</option>
              <option value="bug">Bug</option>
              <option value="improvement">Improvement</option>
              <option value="question">Question</option>
              <option value="administrative">Administrative</option>
            </select>
          </div>

          <div className="sidebar-field">
            <label>Deadline</label>
            <input
              type="date"
              value={request.deadline || ''}
              onChange={e => handleUpdate('deadline', e.target.value || null)}
              style={{ width: '100%', padding: '6px 8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div className="sidebar-field" style={{ marginTop: '16px', borderTop: '1px solid #eee', paddingTop: '16px' }}>
            <button
              onClick={handleConvertToAutomation}
              disabled={convertingToAutomation}
              className="btn btn-convert-automation"
              style={{
                width: '100%',
                padding: '10px 12px',
                background: '#6f42c1',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: convertingToAutomation ? 'wait' : 'pointer',
                fontWeight: 500,
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
            >
              🚀 {convertingToAutomation ? 'Creating...' : 'Convert to Automation'}
            </button>
          </div>

          {/* Watchers Section */}
          <div className="sidebar-field" style={{ marginTop: '16px', borderTop: '1px solid #eee', paddingTop: '16px' }}>
            <label>👁 Watchers ({watchers.length})</label>
            {watchers.length > 0 && (
              <div className="watchers-list" style={{ marginTop: '8px' }}>
                {watchers.map(w => (
                  <div key={w.watcher_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px' }}>
                    <span>{w.user_name}</span>
                    <button
                      onClick={async () => {
                        await automunityAPI.removeRequestWatcher(Number(id), w.user_id);
                        fetchWatchers();
                      }}
                      style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: '14px', padding: '0 4px' }}
                      title="Remove watcher"
                    >✕</button>
                  </div>
                ))}
              </div>
            )}
            {currentUserId && !watchers.find(w => w.user_id === currentUserId) ? (
              <button
                onClick={async () => {
                  await automunityAPI.addRequestWatcher(Number(id), currentUserId);
                  fetchWatchers();
                }}
                style={{ marginTop: '8px', width: '100%', padding: '6px', background: '#e8f4fd', border: '1px solid #b8daff', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', color: '#0056b3' }}
              >
                👁 Watch this request
              </button>
            ) : currentUserId ? (
              <button
                onClick={async () => {
                  await automunityAPI.removeRequestWatcher(Number(id), currentUserId);
                  fetchWatchers();
                }}
                style={{ marginTop: '8px', width: '100%', padding: '6px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', color: '#856404' }}
              >
                🔕 Unwatch
              </button>
            ) : null}
            {/* Admin: add other users as watchers */}
            <div style={{ marginTop: '8px' }}>
              {addingWatcher ? (
                <select
                  onChange={async (e) => {
                    if (e.target.value) {
                      await automunityAPI.addRequestWatcher(Number(id), Number(e.target.value));
                      fetchWatchers();
                      setAddingWatcher(false);
                    }
                  }}
                  onBlur={() => setAddingWatcher(false)}
                  autoFocus
                  style={{ width: '100%', padding: '6px', fontSize: '13px' }}
                >
                  <option value="">Select user...</option>
                  {sortedUsers
                    .filter(u => !watchers.find(w => w.user_id === u.user_id))
                    .map(u => (
                      <option key={u.user_id} value={u.user_id}>{u.full_name}</option>
                    ))
                  }
                </select>
              ) : (
                <button
                  onClick={() => setAddingWatcher(true)}
                  style={{ width: '100%', padding: '6px', background: '#f8f9fa', border: '1px dashed #ccc', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', color: '#666' }}
                >
                  + Add watcher
                </button>
              )}
            </div>
          </div>

          <div className="sidebar-info">
            <div><strong>Created:</strong> {new Date(request.created_at).toLocaleString()}</div>
            {request.updated_at && <div><strong>Updated:</strong> {new Date(request.updated_at).toLocaleString()}</div>}
            {request.resolved_at && <div><strong>Resolved:</strong> {new Date(request.resolved_at).toLocaleString()}</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestView;
